import {
  type FilterValue,
  type Filter,
} from "@shopify/hydrogen-react/storefront-api-types";

import {
  COLLECTION_FILTER_IDS,
  COLLECTION_FILTER_SORT_PARAMS,
} from "@react/sections/Collection/CollectionFilters/constants";
import { currencySymbol } from "@root/tablescape/utilities/currency";
import { type SortOption } from "@tablescape/state";

const isCollectionFilterParamKey = (
  key: string
): key is COLLECTION_FILTER_SORT_PARAMS => {
  return (Object.values(COLLECTION_FILTER_SORT_PARAMS) as string[]).includes(
    key
  );
};

const parsePriceFilter = (queryValue: string): FilterValue | null => {
  const match = queryValue.match(/^(\d+)-(\d+)$/);
  if (!match) return null;

  const min = parseInt(match[1], 10);
  const max = parseInt(match[2], 10);

  if (
    isNaN(min) ||
    isNaN(max) ||
    min < 0 ||
    max < 0 ||
    max > 1000000 ||
    min >= max
  )
    return null;

  return {
    id: COLLECTION_FILTER_IDS.PRICE,
    count: 0,
    input: JSON.stringify({ price: { min, max } }),
    label: `${currencySymbol()}${min} - ${currencySymbol()}${max}`,
  };
};

export const getAppliedSortFromQuery = (availableSorts: SortOption[]) => {
  if (availableSorts.length === 0) return undefined;

  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const sortParam = params.get("sort");
  if (!sortParam) return undefined;

  return availableSorts.find(({ name }) => name === sortParam);
};

export const getAppliedFiltersFromQuery = (
  availableFilters: Filter[] | undefined
): FilterValue[] => {
  if (!availableFilters) return [];

  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const preservedFilters: FilterValue[] = [];

  for (const [queryKey, queryValue] of params.entries()) {
    if (queryKey === COLLECTION_FILTER_SORT_PARAMS.PRICE) {
      const priceFilter = parsePriceFilter(queryValue);
      if (priceFilter) preservedFilters.push(priceFilter);
      continue;
    }
    const filterMatch = availableFilters.find(
      (f) => f.label.toLowerCase() === queryKey
    );
    if (!filterMatch) continue;

    const matchedValues = queryValue
      .split(",")
      .map((val) =>
        filterMatch.values.find(
          (v) => v.label.toLowerCase() === decodeURIComponent(val).trim()
        )
      )
      .filter((v): v is FilterValue => v !== undefined);

    preservedFilters.push(...matchedValues);
  }

  return preservedFilters;
};

export const updateQueryFromFilterSortState = (
  appliedFilters: FilterValue[],
  availableFilters: Filter[] | undefined,
  appliedSort: SortOption | undefined,
  availableSorts: SortOption[]
) => {
  if (!availableFilters) return;
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);

  for (const key of Array.from(params.keys())) {
    if (isCollectionFilterParamKey(key)) {
      params.delete(key);
    }
  }

  const grouped: Record<string, FilterValue[]> = {};
  for (const filterValue of appliedFilters) {
    const filter = availableFilters.find((f) => filterValue.id.includes(f.id));
    if (!filter) continue;

    const key = filter.label.toLowerCase();
    (grouped[key] ||= []).push(filterValue);
  }

  // Set new filter params
  for (const [key, values] of Object.entries(grouped)) {
    if (key === COLLECTION_FILTER_SORT_PARAMS.PRICE) {
      const input = values[0]?.input;
      if (typeof input === "string") {
        try {
          const { price } = JSON.parse(input);
          const { max, min } = price;
          if (typeof min === "number" && typeof max === "number" && min < max) {
            params.set(key, `${min}-${max}`);
          }
        } catch {
          console.error("Invalid price query value");
        }
      }
    } else {
      const encoded = values.map((v) => v.label.toLowerCase()).join(",");
      params.set(key, encoded);
    }
  }

  const sortOptionMatch =
    appliedSort && availableSorts.find(({ name }) => name === appliedSort.name);

  if (sortOptionMatch) {
    params.set("sort", sortOptionMatch.name);
  } else {
    params.delete("sort");
  }

  const newSearch = params.toString();
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ""}`;
  if (typeof window !== "undefined" && window.history?.replaceState) {
    window.history.replaceState(null, "", newUrl);
  }
};
